import { useEffect, useState } from 'react';
import { ApiError, getMe } from './api/client';
import type { MeResponse } from './api/types';
import { decodeClaims } from './auth/claims';
import type { Session } from './auth/session';

function describeError(e: unknown): string {
  return e instanceof ApiError ? `${e.body.code}: ${e.body.description}` : 'Request failed';
}

type MeState =
  | { kind: 'loading' }
  | { kind: 'ok'; me: MeResponse }
  | { kind: 'failed'; message: string };

const left = { textAlign: 'left' } as const;

/**
 * The Identity view: what the access token *claims* (decoded in the browser, unverified,
 * display-only) next to what the server *validated* (GET /api/me — the middleware's view of
 * the same token). The pair proves resource-side validation for whichever IdP signed you in.
 */
export function IdentityPanel({ session }: { session: Session | null }) {
  const [me, setMe] = useState<MeState>({ kind: 'loading' });

  // Re-ask the server whenever the session changes — the bearer token rides apiFetch.
  useEffect(() => {
    setMe({ kind: 'loading' });
    getMe()
      .then((response) => setMe({ kind: 'ok', me: response }))
      .catch((e: unknown) => setMe({ kind: 'failed', message: describeError(e) }));
  }, [session]);

  const claims = session === null ? null : decodeClaims(session.accessToken);

  return (
    <section>
      <h2>Access-token claims (browser-decoded, unverified)</h2>
      {session === null ? (
        <p>Sign in to inspect an access token.</p>
      ) : claims === null ? (
        <p>The access token could not be decoded.</p>
      ) : (
        <table style={{ borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={left}>Claim</th>
              <th style={left}>Value</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(claims).map(([claim, value]) => (
              <tr key={claim}>
                <td>
                  <code>{claim}</code>
                </td>
                <td>
                  <code>{JSON.stringify(value)}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <h2>Server-validated identity (GET /api/me)</h2>
      <p>
        These values come from the backend, not the browser: the ADR-017 middleware validated the
        bearer token against the auth server&rsquo;s keys before answering — proof of
        resource-side validation for whichever identity provider was used to sign in.
      </p>
      {me.kind === 'loading' ? (
        <p>Asking the server…</p>
      ) : me.kind === 'failed' ? (
        <p role="alert" style={{ color: '#b00020' }}>
          {me.message}
        </p>
      ) : me.me.enforced ? (
        <table style={{ borderCollapse: 'collapse' }}>
          <tbody>
            <tr>
              <td>Subject</td>
              <td>
                <code>{me.me.subject ?? '(none)'}</code>
              </td>
            </tr>
            <tr>
              <td>Email</td>
              <td>
                <code>{me.me.email ?? '(none)'}</code>
              </td>
            </tr>
            <tr>
              <td>Scope</td>
              <td>
                <code>{me.me.scope ?? '(none)'}</code>
              </td>
            </tr>
          </tbody>
        </table>
      ) : (
        <p>
          Enforcement is off (a local run without <code>Auth:Issuer</code>): the backend validated
          nothing and sees no identity.
        </p>
      )}
    </section>
  );
}
